import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  InputAdornment,
  Divider,
  Paper,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import { parseJwt, API_BASE, ENDPOINTS } from '../utils/api.js';
import SmsConsentCheckbox from './SmsConsentCheckbox.js';

/* ──────────────────────────────────────────
   Country dial-code list (shared constant)
────────────────────────────────────────── */
const COUNTRIES = [
  { code: 'IN', label: 'India', dial: '+91', flag: '🇮🇳' },
  { code: 'US', label: 'United States', dial: '+1', flag: '🇺🇸' },
  { code: 'GB', label: 'United Kingdom', dial: '+44', flag: '🇬🇧' },
  { code: 'AU', label: 'Australia', dial: '+61', flag: '🇦🇺' },
  { code: 'CA', label: 'Canada', dial: '+1', flag: '🇨🇦' },
  { code: 'AE', label: 'UAE', dial: '+971', flag: '🇦🇪' },
  { code: 'SG', label: 'Singapore', dial: '+65', flag: '🇸🇬' },
];

/* Pick the initial country for the phone input. sqip is Canada-first, and the
   US and Canada are indistinguishable by dial code (+1), so we bias to Canada:
   only auto-select another country when we have a strong, unambiguous signal
   (a Canadian timezone, or an explicit non-US locale region like en-IN / en-GB).
   Everything else — US locale, no region, detection failure — defaults to Canada. */
function detectDefaultCountry() {
  const canada = COUNTRIES.find((c) => c.code === 'CA') || COUNTRIES[0];
  try {
    // 1) Canadian timezone is the strongest signal (beats an en-US locale).
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const CA_TZ = [
      'Toronto', 'Vancouver', 'Edmonton', 'Winnipeg', 'Halifax', 'Montreal',
      'Regina', 'St_Johns', 'Moncton', 'Whitehorse', 'Yellowknife', 'Iqaluit',
    ];
    if (CA_TZ.some((z) => tz.includes(z))) return canada;

    // 2) Explicit, supported, non-US region from the browser locale.
    const locales =
      navigator.languages && navigator.languages.length
        ? navigator.languages
        : [navigator.language || ''];
    for (const loc of locales) {
      const region = (loc.split('-')[1] || '').toUpperCase();
      if (region && region !== 'US') {
        const match = COUNTRIES.find((c) => c.code === region);
        if (match) return match;
      }
    }
  } catch (e) {
    /* navigator/Intl unavailable — fall through to default */
  }
  // 3) Default: Canada (covers US-ambiguous, no region, and detection failure).
  return canada;
}

const defaultCountry = detectDefaultCountry();

/* ──────────────────────────────────────────
   Country + phone input widget
────────────────────────────────────────── */
function PhoneInput({ country, setCountry, phone, setPhone, onEnter, label = 'Phone number', compact = false }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.75, mb: compact ? 1.5 : 2, alignItems: 'stretch' }}>
      {/* Country prefix chip */}
      <Select
        value={country.code}
        onChange={(e) => setCountry(COUNTRIES.find((c) => c.code === e.target.value) || defaultCountry)}
        size="small"
        sx={{
          minWidth: compact ? 82 : 110,
          borderRadius: 2,
          fontWeight: 500,
          '& .MuiSelect-select': { py: compact ? '6px' : '8px', px: 1 },
        }}
        renderValue={(v) => {
          const c = COUNTRIES.find((x) => x.code === v);
          return c ? <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{c.flag} <span style={{ fontSize: compact ? '0.82rem' : '0.9rem' }}>{c.dial}</span></span> : v;
        }}
      >
        {COUNTRIES.map((c) => (
          <MenuItem key={c.code} value={c.code}>
            {c.flag} {c.label} ({c.dial})
          </MenuItem>
        ))}
      </Select>
      {/* Phone number field */}
      <TextField
        fullWidth
        label={label}
        placeholder="9876543210"
        size="small"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && onEnter && onEnter()}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PhoneOutlinedIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
        variant="outlined"
        type="tel"
      />
    </Box>
  );
}

/* ──────────────────────────────────────────
   Main component
────────────────────────────────────────── */
// Split an E.164 phone (e.g. "+14372569894") into a COUNTRIES entry + local digits, so an
// invite-prefilled number lands in the phone field with the right country. Canada-first for +1.
function splitE164(e164) {
  const raw = String(e164 || '').trim();
  if (!raw.startsWith('+')) return { country: defaultCountry, local: raw.replace(/\D/g, '') };
  const matches = COUNTRIES.filter((c) => raw.startsWith(c.dial)).sort((a, b) => b.dial.length - a.dial.length);
  if (matches.length === 0) return { country: defaultCountry, local: raw.replace(/^\+/, '').replace(/\D/g, '') };
  const longest = matches[0].dial.length;
  const top = matches.filter((m) => m.dial.length === longest);
  const chosen = top.find((m) => m.code === defaultCountry.code) || top[0]; // prefer CA for +1
  return { country: chosen, local: raw.slice(chosen.dial.length).replace(/\D/g, '') };
}

function LoginForm({ redirectFrom = '/', preSelectOrg = null, preSelectCat = null, prefillPhone = null, onClose = null, isMobileSheet = false }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  /* ── registration form state ── */
  const [regFirstName, setRegFirstName] = useState('');
  const [regLastName, setRegLastName] = useState('');
  const [regCountry, setRegCountry] = useState(defaultCountry);
  const [regPhone, setRegPhone] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regError, setRegError] = useState('');
  const [regLoading, setRegLoading] = useState(false);

  /* ── login form state (prefilled from an invite phone when present) ── */
  const _prefill = prefillPhone ? splitE164(prefillPhone) : null;
  const [loginIdentifier, setLoginIdentifier] = useState(_prefill ? _prefill.local : '');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginCountry, setLoginCountry] = useState(_prefill ? _prefill.country : defaultCountry);
  const [loginUsePhone, setLoginUsePhone] = useState(true);
  // Two-step admin username login: enter username -> reveal password field.
  const [loginPasswordStep, setLoginPasswordStep] = useState(false);
  const [loginPassword, setLoginPassword] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  /* ── OTP / phone-verification state ── */
  // Whether the backend requires OTP. Fetched once on mount; when false we
  // fall back to the legacy direct register / passwordless login.
  const [otpEnabled, setOtpEnabled] = useState(true);
  // Which delivery channels the backend has enabled (from /api/config).
  const [otpChannels, setOtpChannels] = useState({ sms: true, email: false });
  // Which channel(s) the last OTP was actually delivered on (from send response).
  const [otpSentVia, setOtpSentVia] = useState([]);
  // Which step each flow is on: 'form' (collecting details) or 'otp'.
  const [regStep, setRegStep] = useState('form');
  const [loginStep, setLoginStep] = useState('form');
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  // Seconds left before "Resend code" is allowed again.
  const [resendIn, setResendIn] = useState(0);
  // Dev-only: backend echoes the code when OTP_DEV_ECHO is on.
  const [devCode, setDevCode] = useState('');

  /* ── SMS consent opt-in (required checkbox) ── */
  const [regConsent, setRegConsent] = useState(false);
  const [regConsentError, setRegConsentError] = useState('');
  const [loginConsent, setLoginConsent] = useState(false);
  const [loginConsentError, setLoginConsentError] = useState('');
  const CONSENT_ERROR = 'Please agree to receive SMS messages to continue.';

  /* ── SMS double opt-in (Canadian toll-free) state ── */
  // When set ('register' | 'login'), we're awaiting the user's YES reply and
  // polling the backend for consent status.
  const [consentFlow, setConsentFlow] = useState(null);
  const [consentError, setConsentError] = useState('');
  const [consentWindow, setConsentWindow] = useState(600); // seconds

  useEffect(() => {
    let cancelled = false;
    fetch(ENDPOINTS.CONFIG)
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (!cancelled && cfg && typeof cfg.otp_enabled === 'boolean') {
          setOtpEnabled(cfg.otp_enabled);
          if (cfg.otp_channels) setOtpChannels(cfg.otp_channels);
        }
      })
      .catch(() => {/* default to enabled */});
    return () => { cancelled = true; };
  }, []);

  // Resend cooldown countdown.
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setInterval(() => setResendIn((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [resendIn]);

  // Double opt-in: poll consent status while waiting for the user's YES reply.
  useEffect(() => {
    if (!consentFlow) return undefined;
    const purpose = consentFlow;
    const phone =
      purpose === 'register'
        ? `${regCountry.dial}${regPhone.trim()}`
        : `${loginCountry.dial}${loginIdentifier.trim()}`;
    let stopped = false;
    const setStep = purpose === 'register' ? setRegStep : setLoginStep;

    const advanceToOtp = (data) => {
      setDevCode((data && data.dev_code) || '');
      setOtp('');
      setOtpError('');
      setResendIn(60);
      setConsentFlow(null);
      setStep('otp');
    };

    const poll = async () => {
      try {
        const url = `${ENDPOINTS.CONSENT_STATUS}?phone=${encodeURIComponent(phone)}&purpose=${purpose}`;
        const res = await fetch(url);
        const data = await res.json().catch(() => ({}));
        if (stopped) return;
        switch (data.state) {
          case 'ready':
            advanceToOtp();
            break;
          case 'confirmed': {
            // Consent recorded but no live code yet — request it now (sends directly).
            const { res: r2, data: d2 } = await sendOtp(phone, purpose);
            if (!stopped && r2.ok && (d2.status === 'Success' || d2.code === 'sent')) {
              advanceToOtp(d2);
            }
            break;
          }
          case 'opted_out':
            setConsentFlow(null);
            setConsentError('This number has opted out of SMS. Reply START to opt back in, then try again.');
            break;
          case 'expired':
            setConsentFlow(null);
            setConsentError("We didn't get your confirmation in time. Check your phone, reply YES, then try again.");
            break;
          default:
            break; // 'pending' / 'unknown' → keep polling
        }
      } catch {
        /* transient network error — keep polling */
      }
    };

    poll();
    const interval = setInterval(poll, 4000);
    // Hard stop a little after the server-side window so we don't poll forever.
    const deadline = setTimeout(() => {
      setConsentFlow(null);
      setConsentError("We didn't get your confirmation in time. Check your phone, reply YES, then try again.");
    }, (consentWindow + 5) * 1000);

    return () => {
      stopped = true;
      clearInterval(interval);
      clearTimeout(deadline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentFlow]);

  /* ─────────────────────────────────────── */

  const postLoginRedirect = async (accessToken) => {
    try {
      const res = await fetch(ENDPOINTS.ME, { headers: { Authorization: `Bearer ${accessToken}` } });
      if (res.ok) {
        const me = await res.json();
        localStorage.setItem('isStaff', me.is_staff || me.is_superuser ? 'true' : 'false');
        localStorage.setItem('userGroups', JSON.stringify(me.groups || []));
        // Persist org-admin flags
        localStorage.setItem('isOrgAdmin', me.is_org_admin ? 'true' : 'false');
        localStorage.setItem('isSqipAdmin', me.is_sqip_admin ? 'true' : 'false');
        localStorage.setItem('orgAccess', JSON.stringify(me.org_access || []));
  const isAdmin = me.is_staff || me.is_superuser || me.is_org_admin || (me.groups && me.groups.length > 0);
        window.dispatchEvent(new CustomEvent('sqip:login', { detail: { user: me } }));
        window.dispatchEvent(new CustomEvent('sqip:postLogin', {
          detail: { from: redirectFrom, preSelectOrg, preSelectCat },
        }));
        
        // If this login was opened with a pre-selected org+category (booking flow),
        // prefer not to force a navigation away — close the dialog and let
        // page-specific post-login handlers (they listen for 'sqip:postLogin')
        // open the booking flow in-place. This preserves the user's starting
        // context (e.g., staying on /org/:id) instead of always navigating.
        if (preSelectOrg && preSelectCat) {
          if (onClose) onClose();
          return;
        }

        // Determine redirect destination with proper priority.
        // Priority A: If user is an admin, take them to the admin dashboard by default.
        // Priority B: If a non-admin user has an explicit redirectFrom (and it's not /login), honor it.
        // Note: preSelectOrg+preSelectCat handled above and returns early.
        let destination = '/';
        if (isAdmin) {
          destination = '/admin';
        } else if (redirectFrom && redirectFrom !== '/login') {
          destination = redirectFrom;
        }
        if (onClose) onClose();
        navigate(destination, { replace: true });
      } else {
        if (onClose) onClose();
        navigate(redirectFrom || '/', { replace: true });
      }
    } catch {
      if (onClose) onClose();
      navigate(redirectFrom || '/', { replace: true });
    }
  };

  const storeTokens = (data) => {
    localStorage.setItem('accessToken', data.access);
    window.dispatchEvent(new Event('sqip:loggedIn'));
    localStorage.setItem('userId', data.id);
    localStorage.setItem('username', data.username);
    // persist readable name parts for navbar/profile
    if (data.first_name !== undefined) localStorage.setItem('firstName', data.first_name || '');
    if (data.last_name !== undefined) localStorage.setItem('lastName', data.last_name || '');
    // store access token expiry (ms since epoch) so the app can proactively refresh
    try {
      const payload = parseJwt(data.access);
      if (payload && payload.exp) {
        localStorage.setItem('accessTokenExp', String(payload.exp * 1000));
      }
    } catch (e) {
      // ignore parse/storage errors
    }
  };

  /* ── shared OTP send helper ── */
  const sendOtp = async (phone, purpose, email) => {
    const body = { phone, purpose };
    // Email is only used for register delivery; login resolves it server-side.
    if (purpose === 'register' && email) body.email = email;
    const res = await fetch(ENDPOINTS.SEND_OTP, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    return { res, data };
  };

  // Decide the next UI step from a send/otp success response. Returns true if
  // handled (consent or OTP step set); the caller already cleared loading.
  const applySendResult = (data, flow) => {
    setOtp('');
    setOtpError('');
    setConsentError('');
    setDevCode(data.dev_code || '');
    setOtpSentVia(Array.isArray(data.channels) ? data.channels : []);
    const setStep = flow === 'register' ? setRegStep : setLoginStep;
    if (data.status === 'ConsentPending' || data.code === 'consent_pending') {
      // Double opt-in: a confirmation SMS was sent; wait for the YES reply.
      setConsentWindow(data.consent_window || 600);
      setStep('consent');
      setConsentFlow(flow); // kicks off polling (see effect below)
    } else {
      setResendIn(data.retry_after || 60);
      setStep('otp');
    }
  };

  // Actually create the account. otpCode is undefined when OTP is disabled.
  const doRegister = async (fullPhone, otpCode) => {
    setOtpError('');
    setRegLoading(true);
    setOtpLoading(true);
    try {
      const res = await fetch(ENDPOINTS.REGISTER, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: regFirstName.trim(),
          last_name: regLastName.trim(),
          phone: fullPhone,
          email: regEmail.trim() || undefined,
          otp: otpCode,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        storeTokens(data);
        await postLoginRedirect(data.access);
      } else {
        const err = await res.json().catch(() => ({}));
        const msg = err.errors
          ? Object.values(err.errors).flat().join(' ')
          : (err.message || 'Registration failed.');
        if (otpCode !== undefined) setOtpError(msg); else setRegError(msg);
      }
    } catch {
      const net = 'Network error. Please try again.';
      if (otpCode !== undefined) setOtpError(net); else setRegError(net);
    }
    setRegLoading(false);
    setOtpLoading(false);
  };

  /* ── Register: step 1 — validate then send OTP (or register directly) ── */
  const handleRegister = async () => {
    setRegError('');
    if (!regFirstName.trim()) { setRegError('First name is required.'); return; }
    if (!regPhone.trim()) { setRegError('Phone number is required.'); return; }
    if (!regConsent) { setRegConsentError(CONSENT_ERROR); return; }
    setRegConsentError('');
    const fullPhone = `${regCountry.dial}${regPhone.trim()}`;

    if (!otpEnabled) { doRegister(fullPhone, undefined); return; }

    // Email is required to receive the code when email is a delivery channel.
    if (otpChannels.email && !regEmail.trim()) {
      setRegError('Email is required to receive your verification code.');
      return;
    }

    setRegLoading(true);
    try {
      const { res, data } = await sendOtp(fullPhone, 'register', regEmail.trim());
      if (res.ok) {
        applySendResult(data, 'register');
      } else {
        setRegError(data.message || 'Could not send verification code.');
      }
    } catch { setRegError('Network error. Please try again.'); }
    setRegLoading(false);
  };

  /* ── Register: step 2 — verify code + create ── */
  const handleRegisterVerify = () => {
    if (!otp.trim()) { setOtpError('Enter the code we sent you.'); return; }
    doRegister(`${regCountry.dial}${regPhone.trim()}`, otp.trim());
  };

  // Passwordless phone login (only when OTP is disabled). Admin username login is NOT
  // passwordless — it goes through doPasswordLogin.
  const doDirectLogin = async () => {
    setLoginLoading(true);
    try {
      const identifier = `${loginCountry.dial}${loginIdentifier.trim()}`;
      const res = await fetch(ENDPOINTS.AUTH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier }),
      });
      if (res.ok) {
        const data = await res.json();
        storeTokens(data);
        await postLoginRedirect(data.access);
      } else {
        const err = await res.json().catch(() => ({}));
        setLoginError(err.message || err.detail || 'Login failed. User not found.');
      }
    } catch { setLoginError('Network error. Please try again.'); }
    setLoginLoading(false);
  };

  // Admin username + password login (step 2).
  const doPasswordLogin = async () => {
    setLoginError('');
    if (!loginPassword) { setLoginError('Please enter your password.'); return; }
    setLoginLoading(true);
    try {
      const res = await fetch(ENDPOINTS.PASSWORD_LOGIN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: loginIdentifier.trim(), password: loginPassword }),
      });
      if (res.ok) {
        const data = await res.json();
        storeTokens(data);
        await postLoginRedirect(data.access);
      } else {
        const err = await res.json().catch(() => ({}));
        setLoginError(err.message || 'Invalid username or password.');
      }
    } catch { setLoginError('Network error. Please try again.'); }
    setLoginLoading(false);
  };

  // Self-service admin password reset (generic response — no account enumeration).
  const handleForgotPassword = async () => {
    setForgotMsg(''); setLoginError('');
    if (!loginIdentifier.trim()) { setLoginError('Enter your username first.'); return; }
    setForgotLoading(true);
    try {
      const res = await fetch(ENDPOINTS.FORGOT_PASSWORD, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: loginIdentifier.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      setForgotMsg(data.message || 'If an admin account exists, a reset link has been sent to its email.');
    } catch { setLoginError('Network error. Please try again.'); }
    setForgotLoading(false);
  };

  /* ── Login: phone → send OTP; username → password step ── */
  const handleLogin = async () => {
    setLoginError('');
    if (!loginIdentifier.trim()) {
      setLoginError('Please enter your phone number or username.');
      return;
    }
    // Username (admin) login: reveal the password field, then doPasswordLogin.
    if (!loginUsePhone) { setForgotMsg(''); setLoginPasswordStep(true); return; }
    // Phone with OTP disabled: passwordless direct login.
    if (!otpEnabled) { doDirectLogin(); return; }

    // Phone login sends SMS, so consent is required.
    if (!loginConsent) { setLoginConsentError(CONSENT_ERROR); return; }
    setLoginConsentError('');

    const fullPhone = `${loginCountry.dial}${loginIdentifier.trim()}`;
    setLoginLoading(true);
    try {
      const { res, data } = await sendOtp(fullPhone, 'login');
      if (res.ok) {
        applySendResult(data, 'login');
      } else {
        setLoginError(data.message || 'Could not send verification code.');
      }
    } catch { setLoginError('Network error. Please try again.'); }
    setLoginLoading(false);
  };

  /* ── Login: step 2 — verify code + issue tokens ── */
  const handleLoginVerify = async () => {
    setOtpError('');
    if (!otp.trim()) { setOtpError('Enter the code we sent you.'); return; }
    setOtpLoading(true);
    try {
      const res = await fetch(ENDPOINTS.VERIFY_OTP, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: `${loginCountry.dial}${loginIdentifier.trim()}`, otp: otp.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        storeTokens(data);
        await postLoginRedirect(data.access);
      } else {
        const err = await res.json().catch(() => ({}));
        setOtpError(err.message || 'Invalid code.');
      }
    } catch { setOtpError('Network error. Please try again.'); }
    setOtpLoading(false);
  };

  /* ── Resend / back ── */
  const handleResend = async () => {
    if (resendIn > 0 || otpLoading) return;
    setOtpError('');
    const isReg = regStep === 'otp';
    const phone = isReg
      ? `${regCountry.dial}${regPhone.trim()}`
      : `${loginCountry.dial}${loginIdentifier.trim()}`;
    setOtpLoading(true);
    try {
      const email = isReg ? regEmail.trim() : undefined;
      const { res, data } = await sendOtp(phone, isReg ? 'register' : 'login', email);
      if (res.ok) {
        setDevCode(data.dev_code || '');
        if (Array.isArray(data.channels)) setOtpSentVia(data.channels);
        setResendIn(data.retry_after || 60);
      } else {
        setOtpError(data.message || 'Could not resend code.');
        if (data.retry_after) setResendIn(data.retry_after);
      }
    } catch { setOtpError('Network error. Please try again.'); }
    setOtpLoading(false);
  };

  const handleOtpBack = () => {
    setRegStep('form'); setLoginStep('form');
    setOtp(''); setOtpError(''); setDevCode('');
  };

  /* ── Consent step: back / try again ── */
  const handleConsentBack = () => {
    setConsentFlow(null);
    setConsentError('');
    setRegStep('form'); setLoginStep('form');
  };

  const handleConsentRetry = () => {
    setConsentError('');
    if (regStep === 'consent') handleRegister();
    else handleLogin();
  };

  /* ─────────────────────────────────────── */

  // Shared content — used by both desktop and mobile
  // compact = true means tighter spacing / smaller inputs
  const compact = isMobileSheet || isXs;
  const inputSize = compact ? 'small' : 'medium';
  const px = compact ? 2.5 : 4;
  const pt = compact ? 2 : 4;
  const pb = compact ? 2 : 4;

  const inOtpStep = regStep === 'otp' || loginStep === 'otp';
  const inConsentStep = regStep === 'consent' || loginStep === 'consent';
  const otpTargetPhone = regStep === 'otp'
    ? `${regCountry.dial}${regPhone}`
    : `${loginCountry.dial}${loginIdentifier}`;
  const consentTargetPhone = regStep === 'consent'
    ? `${regCountry.dial}${regPhone}`
    : `${loginCountry.dial}${loginIdentifier}`;
  // Which channel(s) the code went out on — prefer the actual send response,
  // else fall back to the configured channels.
  const otpChannelsUsed = otpSentVia.length
    ? otpSentVia
    : [otpChannels.sms && 'sms', otpChannels.email && 'email'].filter(Boolean);
  const otpViaSms = otpChannelsUsed.includes('sms');
  const otpViaEmail = otpChannelsUsed.includes('email');
  // We only know the email address on register; on login it's server-resolved.
  const otpTargetEmail = regStep === 'otp' ? (regEmail.trim() || 'your email') : 'your email';

  const consentPanel = (
    <Box sx={{ px, pt, pb, width: '100%' }}>
      <Typography sx={{ fontWeight: 800, fontSize: 20, mb: 0.5, color: 'text.primary' }}>
        Confirm SMS consent
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        To meet carrier rules, we've texted <strong>{consentTargetPhone}</strong> to confirm.
        Reply <strong>YES</strong> to that message and we'll send your verification code
        automatically.
      </Typography>

      {consentFlow && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Waiting for your reply…
          </Typography>
        </Box>
      )}

      {consentError && (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2, py: compact ? 0 : 0.5 }}>
          {consentError}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
        <Button size="small" onClick={handleConsentBack} sx={{ textTransform: 'none' }}>
          ← Back
        </Button>
        {!consentFlow && (
          <Button size="small" onClick={handleConsentRetry} sx={{ textTransform: 'none' }}>
            Try again
          </Button>
        )}
      </Box>
    </Box>
  );

  const otpPanel = (
    <Box sx={{ px, pt, pb, width: '100%' }}>
      <Typography sx={{ fontWeight: 800, fontSize: 20, mb: 0.5, color: 'text.primary' }}>
        {otpViaEmail && !otpViaSms ? 'Verify your email' : 'Verify your phone'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {otpViaSms && otpViaEmail ? (
          <>Enter the code we sent via SMS to <strong>{otpTargetPhone}</strong> and by email to <strong>{otpTargetEmail}</strong>.</>
        ) : otpViaEmail ? (
          <>Enter the code we emailed to <strong>{otpTargetEmail}</strong>.</>
        ) : (
          <>Enter the code we just sent via SMS to <strong>{otpTargetPhone}</strong>.</>
        )}
      </Typography>

      {devCode && (
        <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2 }}>
          Dev mode — your code is <strong>{devCode}</strong>
        </Alert>
      )}

      <TextField
        fullWidth
        autoFocus
        size={inputSize}
        label="Verification code"
        placeholder="123456"
        value={otp}
        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 8))}
        onKeyDown={(e) => e.key === 'Enter' && (regStep === 'otp' ? handleRegisterVerify() : handleLoginVerify())}
        inputProps={{ inputMode: 'numeric', style: { letterSpacing: '0.3em', fontSize: '1.15rem' } }}
        variant="outlined"
        sx={{ mb: 1.5 }}
      />

      {otpError && (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2, py: compact ? 0 : 0.5 }}>
          {otpError}
        </Alert>
      )}

      <Button
        fullWidth
        variant="contained"
        color="primary"
        size={compact ? 'medium' : 'large'}
        onClick={regStep === 'otp' ? handleRegisterVerify : handleLoginVerify}
        disabled={otpLoading || otp.trim().length < 4}
        sx={{ py: compact ? 0.9 : 1.4, fontSize: compact ? '0.92rem' : '1rem', borderRadius: 3, mb: 1 }}
      >
        {otpLoading
          ? <CircularProgress size={20} color="inherit" />
          : (regStep === 'otp' ? 'Verify & Create Account' : 'Verify & Sign In')}
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
        <Button size="small" onClick={handleOtpBack} sx={{ textTransform: 'none' }}>
          ← Back
        </Button>
        <Button
          size="small"
          onClick={handleResend}
          disabled={resendIn > 0 || otpLoading}
          sx={{ textTransform: 'none' }}
        >
          {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
        </Button>
      </Box>
    </Box>
  );

  const formContent = (
    <Box sx={{ px, pt, pb, width: '100%' }}>

      {/* ────── Header ────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: compact ? 2 : 3 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: (theme) =>
              `0 2px 8px ${theme.palette.custom ? 'rgba(130,172,87,0.14)' : 'rgba(0,0,0,0.08)'}`,
            background: (theme) =>
              theme.palette.custom
                ? theme.palette.custom.green || '#82ac57'
                : '#82ac57',
          }}
        >
          <EventNoteOutlinedIcon sx={{ color: '#fff', fontSize: 26 }} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: 24,
              letterSpacing: -0.5,
              color: 'text.primary',
              lineHeight: 1.1,
            }}
          >
            sqip
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: 12,
              letterSpacing: 0.3,
              color: 'text.secondary',
              textTransform: 'lowercase',
              lineHeight: 1,
            }}
          >
            skip the queue
          </Typography>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: compact ? 2 : 3, fontSize: compact ? '0.82rem' : '0.875rem' }}>
        Create an account to book appointments seamlessly.
      </Typography>

      {/* ────── First + Last name ────── */}
      <Box sx={{ display: 'flex', gap: 1, mb: compact ? 1.5 : 2 }}>
        <TextField
          sx={{ flex: 1 }}
          size={inputSize}
          label="First name"
          placeholder="First name"
          value={regFirstName}
          onChange={(e) => setRegFirstName(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <PersonOutlineIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          variant="outlined"
        />
        <TextField
          sx={{ flex: 1 }}
          size={inputSize}
          label="Last name"
          placeholder="Last name"
          value={regLastName}
          onChange={(e) => setRegLastName(e.target.value)}
          variant="outlined"
        />
      </Box>

      {/* ────── Phone ────── */}
      <PhoneInput
        compact={compact}
        country={regCountry}
        setCountry={setRegCountry}
        phone={regPhone}
        setPhone={setRegPhone}
        onEnter={handleRegister}
        label="Phone number"
      />

      {/* ────── Email ────── */}
      <TextField
        fullWidth
        size={inputSize}
        required={otpChannels.email}
        label={otpChannels.email ? 'Email' : 'Email (optional)'}
        helperText={otpChannels.email ? "We'll email your verification code here." : undefined}
        placeholder="your@email.com"
        type="email"
        value={regEmail}
        onChange={(e) => setRegEmail(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && handleRegister()}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <EmailOutlinedIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
        variant="outlined"
        sx={{ mb: compact ? 1.5 : 2 }}
      />

      {regError && (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2, py: compact ? 0 : 0.5, fontSize: compact ? '0.8rem' : undefined }}>
          {regError}
        </Alert>
      )}

      {/* ────── SMS consent opt-in (CTIA) — required before phone submit ────── */}
      <SmsConsentCheckbox
        checked={regConsent}
        onChange={(v) => { setRegConsent(v); if (v) setRegConsentError(''); }}
        error={regConsentError}
      />

      {/* ────── Create Account button ────── */}
      <Button
        fullWidth
        variant="contained"
        color="primary"
        size={compact ? 'medium' : 'large'}
        onClick={handleRegister}
        disabled={regLoading || !regFirstName || !regPhone}
        sx={{
          py: compact ? 0.9 : 1.5,
          fontSize: compact ? '0.92rem' : '1rem',
          letterSpacing: 0.4,
          mb: compact ? 0.5 : 1,
          borderRadius: 3,
          boxShadow: (t) => t.shadows[6],
        }}
      >
        {regLoading ? <CircularProgress size={20} color="inherit" /> : 'Create Account'}
      </Button>

      {/* ────── Divider ────── */}
      <Divider sx={{ my: compact ? 1.5 : 3 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', px: 0.5 }}>
          Already have an account?
        </Typography>
      </Divider>

      {/* ────── Sign in label ────── */}
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: compact ? '0.82rem' : '0.875rem', color: 'text.secondary' }}>
        Sign in
      </Typography>

      {/* ────── Toggle phone / username ────── */}
      <Box sx={{ display: 'flex', gap: 0.75, mb: compact ? 1.5 : 2 }}>
        <Button
          size="small"
          variant={loginUsePhone ? 'contained' : 'outlined'}
          onClick={() => { setLoginUsePhone(true); setLoginIdentifier(''); setLoginError(''); setLoginPasswordStep(false); setLoginPassword(''); setForgotMsg(''); }}
          sx={{ textTransform: 'none', flex: 1, fontSize: compact ? '0.78rem' : '0.8rem', py: compact ? 0.5 : 0.6, borderRadius: 2 }}
        >
          Phone
        </Button>
        <Button
          size="small"
          variant={!loginUsePhone ? 'contained' : 'outlined'}
          onClick={() => { setLoginUsePhone(false); setLoginIdentifier(''); setLoginError(''); setLoginPasswordStep(false); setLoginPassword(''); setForgotMsg(''); }}
          sx={{ textTransform: 'none', flex: 1, fontSize: compact ? '0.78rem' : '0.8rem', py: compact ? 0.5 : 0.6, borderRadius: 2 }}
        >
          Username
        </Button>
      </Box>

      {/* ────── Phone or Username input ────── */}
      {loginUsePhone ? (
        <PhoneInput
          compact={compact}
          country={loginCountry}
          setCountry={setLoginCountry}
          phone={loginIdentifier}
          setPhone={setLoginIdentifier}
          onEnter={handleLogin}
          label="Your phone number"
        />
      ) : (
        <>
          <TextField
            fullWidth
            size={inputSize}
            label="Username"
            placeholder="your username"
            value={loginIdentifier}
            onChange={(e) => setLoginIdentifier(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (loginPasswordStep ? doPasswordLogin() : handleLogin())}
            disabled={loginPasswordStep}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            variant="outlined"
            sx={{ mb: compact ? 1.5 : 2 }}
          />
          {loginPasswordStep && (
            <TextField
              fullWidth
              autoFocus
              size={inputSize}
              type="password"
              label="Password"
              placeholder="your password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && doPasswordLogin()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              variant="outlined"
              sx={{ mb: 1 }}
            />
          )}
          {loginPasswordStep && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: compact ? 1.5 : 2 }}>
              <Button size="small" onClick={() => { setLoginPasswordStep(false); setLoginPassword(''); setLoginError(''); setForgotMsg(''); }} sx={{ textTransform: 'none', fontSize: '0.78rem' }}>
                ← Change username
              </Button>
              <Button size="small" onClick={handleForgotPassword} disabled={forgotLoading} sx={{ textTransform: 'none', fontSize: '0.78rem' }}>
                {forgotLoading ? 'Sending…' : 'Forgot password?'}
              </Button>
            </Box>
          )}
          {forgotMsg && (
            <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2, py: compact ? 0 : 0.5, fontSize: compact ? '0.8rem' : undefined }}>
              {forgotMsg}
            </Alert>
          )}
        </>
      )}

      {loginError && (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: 2, py: compact ? 0 : 0.5, fontSize: compact ? '0.8rem' : undefined }}>
          {loginError}
        </Alert>
      )}

      {/* ────── SMS consent opt-in (CTIA) — required, phone login only ────── */}
      {loginUsePhone && (
        <SmsConsentCheckbox
          checked={loginConsent}
          onChange={(v) => { setLoginConsent(v); if (v) setLoginConsentError(''); }}
          error={loginConsentError}
        />
      )}

      {/* ────── Sign In button (username: "Continue" → password step → "Sign in") ────── */}
      <Button
        fullWidth
        variant="outlined"
        size={compact ? 'medium' : 'large'}
        onClick={(!loginUsePhone && loginPasswordStep) ? doPasswordLogin : handleLogin}
        disabled={loginLoading || !loginIdentifier || (!loginUsePhone && loginPasswordStep && !loginPassword)}
        sx={{ py: compact ? 0.9 : 1.5, fontSize: compact ? '0.92rem' : '1rem', letterSpacing: 0.4, mb: compact ? 1 : 1, borderRadius: 3 }}
      >
        {loginLoading ? <CircularProgress size={20} color="inherit" />
          : (!loginUsePhone && !loginPasswordStep) ? 'Continue' : 'Sign In'}
      </Button>

    </Box>
  );

  let activeContent = formContent;
  if (inOtpStep) activeContent = otpPanel;
  else if (inConsentStep) activeContent = consentPanel;

  // On mobile sheet: no wrapper Paper (the sheet itself is the card)
  if (isMobileSheet) {
    return activeContent;
  }

  // On desktop /login page: wrap with Paper so the page still has the card look
  return (
    <Paper elevation={0} sx={{ p: 0, borderRadius: 0 }}>
      {activeContent}
    </Paper>
  );
}

export default LoginForm;
